import React, { useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { updateTextSize } from 'auto-text-size';

import ChartIntroLabel from '../ChartIntroLabel/ChartIntroLabel';
import NumberCounter from '../NumberCounter/NumberCounter';

import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import useWindowWidth from '../../hooks/useWindowWidth';
import useDelayedEffect from '../../hooks/useDelayedEffect';

import { AnimationsActiveContext } from '../../containers/BasePage';

import { slugify } from '../../utils/helpers';

import s from './BigNumbersBlock.module.scss';

const TEXT_COLOR_LIGHT = 'text-light';
const TEXT_COLOR_DARK = 'text-dark';

const THEME_PINK_PURPLE = 'pink-purple';
const THEME_PINK_ORANGE = 'pink-orange';
const THEME_BLUE_PURPLE = 'blue-purple';
const THEME_BLUE_GREEN = 'blue-green';

const BigNumbersBlock = (props) => {
  const {
    title,
    description,
    infoTitle,
    infoText,
    infoLink,
    counterAnimationDuration,
    items,
    theme,
    textColor,
  } = props;

  const { animationsActive } = useContext(AnimationsActiveContext) || {};

  const titleSlug = slugify(title);
  const [activeTabIdx, setActiveTabIdx] = React.useState(0);

  const [inViewRef, isVisible] = useIntersectionObserver(0.5, undefined, true);
  const windowWidth = useWindowWidth();

  const textSizeContainerRefs = useRef([]);
  const textSizeInnerRefs = useRef([]);

  const handleUpdateTextSize = (i) => (v) => {
    if (!textSizeInnerRefs.current[i]) {
      return;
    }

    updateTextSize({
      containerEl: textSizeContainerRefs.current[i],
      innerEl: textSizeInnerRefs.current[i],
      maxFontSizePx: 230,
    });
  };

  const handleTabSelect = (index) => {
    setActiveTabIdx(index);
  };

  const updateAllTabTextSizes = () => {
    textSizeInnerRefs.current.forEach((el, i) => {
      if (!el) {
        return;
      }

      updateTextSize({
        containerEl: textSizeContainerRefs.current[i],
        innerEl: el,
        maxFontSizePx: 230,
      });
    });
  };

  // update value text sizes on window resize
  useEffect(() => {
    if (textSizeInnerRefs.current.length === 0) {
      return;
    }

    updateAllTabTextSizes();
  }, [windowWidth, isVisible]);

  // update value text sizes manually if we have no enter animation
  useDelayedEffect(
    300,
    () => {
      if (counterAnimationDuration === 0 || !animationsActive) {
        updateAllTabTextSizes();
      }
    },
    [isVisible, counterAnimationDuration]
  );

  const rootCls = classNames(s.Root, 'grid', {
    [s.Visible]: isVisible,

    [s.TextColorDark]: textColor === TEXT_COLOR_DARK,

    [s.ThemePinkPurple]: theme === THEME_PINK_PURPLE,
    [s.ThemePinkOrange]: theme === THEME_PINK_ORANGE,
    [s.ThemeBluePurple]: theme === THEME_BLUE_PURPLE,
    [s.ThemeBlueGreen]: theme === THEME_BLUE_GREEN,
  });

  return (
    <div className={rootCls}>
      <div ref={inViewRef} className={s.Container}>
        <ChartIntroLabel
          titleId={titleSlug}
          title={title}
          description={description}
          infoTitle={infoTitle}
          infoText={infoText}
          infoLink={infoLink}
          theme={textColor === TEXT_COLOR_LIGHT ? 'light' : 'dark'}
        />
        <div className={s.StatsContainer}>
          {items.length > 1 && (
            <div className={s.Tabs}>
              {items.map((item, i) => {
                const containerWidth = (1 / items.length) * 100;
                const tabIndicatorCls = classNames(s.TabIndicator, {
                  [s.TabActive]: i === activeTabIdx,
                });

                return (
                  <div
                    key={slugify(item.title)}
                    className={s.Tab}
                    style={{
                      width: `${containerWidth}%`,
                    }}>
                    <button
                      className={s.TabButton}
                      onClick={() => handleTabSelect(i)}>
                      <span className={s.TabButtonText}>{item.title}</span>
                    </button>
                    <div className={tabIndicatorCls}></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className={s.Stats}>
          {items.map((item, i) => {
            const tabValueActive = i === activeTabIdx;
            const statCls = classNames(s.Stat, {
              [s.StatActive]: tabValueActive,
            });

            return (
              <div key={slugify(item.title)} className={statCls}>
                <div
                  className={s.StatValue}
                  ref={(el) => (textSizeContainerRefs.current[i] = el)}>
                  <span ref={(el) => (textSizeInnerRefs.current[i] = el)}>
                    <NumberCounter
                      from={0}
                      to={item.value}
                      duration={animationsActive ? counterAnimationDuration : 0}
                      active={isVisible && tabValueActive}
                      onPlay={handleUpdateTextSize(i)}
                      onComplete={handleUpdateTextSize(i)}
                      onUpdate={handleUpdateTextSize(i)}
                    />
                    {item.valueUnit && <>&nbsp;{item.valueUnit}</>}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

BigNumbersBlock.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  counterAnimationDuration: PropTypes.number,
  infoTitle: PropTypes.string,
  infoText: PropTypes.string,
  infoLink: PropTypes.object,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      value: PropTypes.number,
      valueUnit: PropTypes.string,
    })
  ),
  textColor: PropTypes.oneOf([TEXT_COLOR_DARK, TEXT_COLOR_LIGHT]),
  theme: PropTypes.oneOf([
    THEME_PINK_PURPLE,
    THEME_PINK_ORANGE,
    THEME_BLUE_PURPLE,
    THEME_BLUE_GREEN,
  ]),
};

BigNumbersBlock.defaultProps = {
  title: '',
  description: '',
  counterAnimationDuration: 1.6,
  items: [],
  textColor: TEXT_COLOR_LIGHT,
  theme: THEME_PINK_ORANGE,
};

export default BigNumbersBlock;
